import { useMemo } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { useUnistyles } from "react-native-unistyles";
import { Card, Text, View } from "@src/components";
import type { RoadSenseScenario, RoadSenseSite } from "@visioni/a3s-reports/client-messages";

import "leaflet/dist/leaflet.css";

interface RoadSenseSiteMapProps {
  /** The report's single site — its address is shown in every scenario popup. */
  site: RoadSenseSite;
  /** Scenario entries; renders a placeholder card when none carry coordinates. */
  scenarios: readonly RoadSenseScenario[];
  /** Min map height in pixels. Defaults to 360 to match the trend chart pane. */
  minHeight?: number;
}

/**
 * A placed scenario — one that carries both coordinates, so it maps to a marker.
 * Narrowing to this shape once keeps the marker render free of optional-coord
 * guards and lets `direction` (optional) drive the flow arrow independently.
 */
interface PlacedScenario {
  pin_number: number;
  name: string;
  asset_id: string;
  total_crossings: number;
  map_lat: number;
  map_lon: number;
  direction?: number;
}

/**
 * Interactive Leaflet site map for the RoadSense report (web only).
 *
 * RoadSense-owned — it follows the same Leaflet conventions as WasteSense's map
 * (Esri imagery base + Carto labels, custom div pins, fit-to-bounds) but is a
 * **separate component** with no shared module. RoadSense's report is currently
 * read-only, so unlike WasteSense this map has no list-sync focus machinery:
 * one pin per placed scenario, a popup with the scenario's crossings + the
 * shared site address, and (when the scenario has a `direction`) a flow arrow
 * rotated to that compass bearing.
 */
export function RoadSenseSiteMap({ site, scenarios, minHeight = 360 }: RoadSenseSiteMapProps) {
  const { theme } = useUnistyles();
  // Only scenarios with coordinates get a marker; the rest still appear in the
  // payload's list elsewhere, they just have no pin (mirrors the server-side
  // static map, which also skips coordinate-less scenarios).
  const points = useMemo<PlacedScenario[]>(
    () =>
      scenarios.filter(
        (s): s is PlacedScenario =>
          s.map_lat !== undefined &&
          s.map_lon !== undefined &&
          Number.isFinite(s.map_lat) &&
          Number.isFinite(s.map_lon),
      ),
    [scenarios],
  );

  // Marker colour comes from the theme token, not a hardcoded hex: Leaflet
  // renders each pin from an HTML string outside React's tree, so it can't read
  // Unistyles tokens at paint time — we resolve `teal` here where the theme is
  // in scope and interpolate it into the markup.
  const pinColor = theme.colors.teal[500];

  const initialBounds = useMemo<L.LatLngBoundsLiteral | null>(() => {
    if (points.length < 2) return null;
    return points.map((p) => [p.map_lat, p.map_lon] as [number, number]);
  }, [points]);

  const initialCenter = useMemo<[number, number] | null>(() => {
    if (points.length !== 1) return null;
    const p = points[0]!;
    return [p.map_lat, p.map_lon];
  }, [points]);

  if (points.length === 0) {
    return (
      <Card
        flex={1}
        minHeight={minHeight}
        alignItems="center"
        justifyContent="center"
        bg="muted"
        borderColor="borderCard"
      >
        <Text textStyle="xs/semibold" color="textMuted" letterSpacing={1} textTransform="uppercase">
          No location data
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          This report did not include marker coordinates.
        </Text>
      </Card>
    );
  }

  // `maxZoom` caps both the auto-fit AND user zoom so we never request tiles
  // past what Esri World_Imagery reliably serves (z18 globally) — otherwise a
  // one-pin regional site gets framed past available imagery and shows only the
  // grey "Map data not yet available" placeholder.
  const sharedMapProps = {
    scrollWheelZoom: true,
    maxZoom: ESRI_IMAGERY_MAX_ZOOM,
    style: { height: "100%", width: "100%" },
  } as const;

  return (
    <View
      flex={1}
      minHeight={minHeight}
      rounded={8}
      borderWidth={1}
      borderColor="borderCard"
      style={{ overflow: "hidden" }}
    >
      <MapContainer
        {...sharedMapProps}
        {...(initialBounds ?
          { bounds: initialBounds, boundsOptions: { padding: [32, 32], maxZoom: ESRI_IMAGERY_MAX_ZOOM } }
        : { center: initialCenter ?? [0, 0], zoom: SINGLE_POINT_ZOOM })}
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles &copy; Esri"
          maxZoom={ESRI_IMAGERY_MAX_ZOOM}
        />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          subdomains={["a", "b", "c", "d"]}
          opacity={0.95}
          maxZoom={ESRI_IMAGERY_MAX_ZOOM}
        />

        {points.map((p) => (
          <Marker
            key={p.pin_number}
            position={[p.map_lat, p.map_lon]}
            icon={buildPinIcon(pinColor, p.direction)}
            title={p.name}
          >
            <Popup>
              <strong>{p.name}</strong>
              <br />
              <small>{site.site_address}</small>
              <br />
              <small>{p.total_crossings.toLocaleString()} crossings</small>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </View>
  );
}

/**
 * Maximum zoom level the Esri World_Imagery basemap reliably serves. Globally
 * Esri publishes imagery to z18; capping `maxZoom` here keeps the tile layer
 * from requesting non-existent tiles (rendered as the grey placeholder).
 */
const ESRI_IMAGERY_MAX_ZOOM = 18;

/** Default zoom when there's only one marker — z16 frames a city block. */
const SINGLE_POINT_ZOOM = 16;

/**
 * Builds the marker icon from a resolved colour token. Kept as a factory (not a
 * module constant) so the dot follows the theme's `teal` token instead of a
 * hardcoded hex — see the call site for why the token is resolved in-component.
 *
 * A teal dot — a deliberately different hue from WasteSense's rose pin so the
 * two reports read as distinct at a glance, and to lean toward the "data
 * signal" side of the brand rather than the incident/alert rose.
 *
 * When the scenario carries a `direction` (compass degrees clockwise from
 * north), a flow arrow is drawn from the dot pointing along that bearing — the
 * on-screen counterpart of the server-rendered static map's arrow, so the web
 * and PDF reports read the same way. The arrow is drawn pointing up (north) and
 * CSS-rotated by `direction`, because a CSS `rotate` is clockwise and 0° up ≡
 * due north — matching the compass convention exactly.
 *
 * The dot is flex-centred inside its icon box so the box's geometric centre IS
 * the dot's visual centre, keeping the `iconAnchor` arithmetic trivial. The box
 * is sized (`PIN_BOX`) to fit the arrow at any rotation so no direction clips.
 *
 * `popupAnchor` depends on whether an arrow is drawn: the arrow rotates with the
 * bearing, so it can point in ANY direction (a southbound flow points the arrow
 * *down*). Anchoring just above the dot would let a north-ish arrow clip the
 * popup, but anchoring above the arrow's fully-extended reach would leave a big
 * gap for undirected/south pins. Since the box already bounds the arrow at every
 * rotation, we anchor the popup at the box top when an arrow is present (clears
 * it in all directions) and keep the tight dot-top anchor when it isn't.
 */
const PIN_BOX = 56;
/** Dot diameter (px) — the marker's anchor point. */
const DOT_SIZE = 14;
/** Arrow shaft length (px) measured from the dot centre. */
const ARROW_LENGTH = 20;

function buildPinIcon(color: string, direction?: number): L.DivIcon {
  const glow = hexToRgba(color, 0.25);
  const dot = `<span style="
    display:block;
    width:${DOT_SIZE}px;
    height:${DOT_SIZE}px;
    border-radius:50%;
    background:${color};
    box-shadow:0 0 0 3px ${glow}, 0 1px 2px rgba(0,0,0,0.4);
  "></span>`;

  const hasArrow = direction !== undefined;
  return L.divIcon({
    className: "roadsense-map-pin",
    html: `<span style="
    position:relative;
    display:flex;
    align-items:center;
    justify-content:center;
    width:${PIN_BOX}px;
    height:${PIN_BOX}px;
  ">${hasArrow ? arrowSvg(color, direction) : ""}${dot}</span>`,
    iconSize: [PIN_BOX, PIN_BOX],
    iconAnchor: [PIN_BOX / 2, PIN_BOX / 2],
    // Box top when an arrow is present (clears it at any rotation), else just
    // above the dot for a tight popup on undirected pins.
    popupAnchor: [0, hasArrow ? -(PIN_BOX / 2) : -(DOT_SIZE / 2) - 4],
  });
}

/**
 * A flow-direction arrow centred on the pin, pointing up (north) then rotated
 * clockwise by `bearingDeg` to the compass bearing. Positioned absolute and
 * centred over the box so its rotation origin is the dot centre; `pointer-events`
 * are off so it never intercepts the marker's own click/popup.
 */
function arrowSvg(color: string, bearingDeg: number): string {
  const c = PIN_BOX / 2;
  // Shaft runs upward from just outside the dot to `ARROW_LENGTH` beyond it.
  const tailY = c - DOT_SIZE / 2;
  const tipY = c - DOT_SIZE / 2 - ARROW_LENGTH;
  const head = 5;
  return `<svg width="${PIN_BOX}" height="${PIN_BOX}" viewBox="0 0 ${PIN_BOX} ${PIN_BOX}" style="
    position:absolute;
    top:0;
    left:0;
    transform:rotate(${bearingDeg}deg);
    transform-origin:${c}px ${c}px;
    pointer-events:none;
  "><g stroke="${color}" stroke-width="3" stroke-linecap="round" fill="none">
    <line x1="${c}" y1="${tailY}" x2="${c}" y2="${tipY}" />
    <line x1="${c}" y1="${tipY}" x2="${c - head}" y2="${tipY + head}" />
    <line x1="${c}" y1="${tipY}" x2="${c + head}" y2="${tipY + head}" />
  </g></svg>`;
}

/** `#RRGGBB` → `rgba(r,g,b,a)` so the marker glow tracks the same colour token. */
function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
